import React, { useCallback, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  webLoadingTrue,
  webLoadingFalse,
} from "../../services/navigate.service";
import { showToast } from "../../services/toast.service";
import { showApiError } from "../../services/apiError";
import { getDetailCompanies } from "../../services/company.service";
import {
  getFinancialReportOptions,
  generateFinancialReport,
  ReportType,
  OutputType,
  PeriodMode,
  CompareMode,
  type GenerateReportPayload,
  type FinancialReportOptions,
  type TransactionMonthOption,
  type ExportResponse,
} from "../../services/financialReports.service";

const REPORT_TYPE_LABELS: Record<number, string> = {
  [ReportType.IncomeStatement]: "Income Statement",
  [ReportType.BalanceSheet]: "Balance Sheet",
  [ReportType.CashFlow]: "Cash Flow",
  [ReportType.TrialBalance]: "Trial Balance",
  [ReportType.ArAgeing]: "AR Ageing",
  [ReportType.ApAgeing]: "AP Ageing",
  [ReportType.ControlAccountReconciliation]: "Control Account Reconciliation",
};

/** Ageing bucket columns (AR/AP ageing reports). Order matches the report layout:
 *  Current is not-yet-due; the rest are days-overdue bands. `amount` carries the row total. */
const AGE_COLS: { key: string; label: string }[] = [
  { key: "current", label: "Current" },
  { key: "d1_30", label: "1-30" },
  { key: "d31_60", label: "31-60" },
  { key: "d61_90", label: "61-90" },
  { key: "d90Plus", label: "90+" },
];

/** Display title for a report: the English label for its type, translated via
 *  the runtime language map when an entry exists (else the English fallback).
 *  Never render the raw i18n key (e.g. "reports.financial.income_statement"). */
const resolveReportTitle = (
  reportType: number | undefined,
  language: any,
): string => {
  const en =
    (reportType != null ? REPORT_TYPE_LABELS[reportType] : undefined) ?? "Report";
  return language?.[en] ?? en;
};

const OUTPUT_TYPE_LABELS: Record<number, string> = {
  [OutputType.View]: "View on screen",
  [OutputType.Pdf]: "PDF",
  [OutputType.Excel]: "Excel",
  [OutputType.Csv]: "CSV",
};

const COUNTRY_ERROR_MSG =
  "Company country is not configured. Please reload and try again.";

const defaultFromDate = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  d.setDate(1);
  return d;
};
const defaultToDate = () => {
  const d = new Date();
  d.setDate(0);
  return d;
};

const FinancialReports = () => {
  const companyId = localStorage.getItem("companyId") || "";
  const locationId = localStorage.getItem("locationId") || undefined;

  const [language, setLanguage] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<FinancialReportOptions | null>(null);
  const [transactionMonths, setTransactionMonths] = useState<TransactionMonthOption[]>([]);
  const [countryId, setCountryId] = useState<string | null>(null);
  const [countryIdChecked, setCountryIdChecked] = useState(false);

  const [reportType, setReportType] = useState<number>(ReportType.IncomeStatement);
  const [fromDate, setFromDate] = useState<Date>(defaultFromDate());
  const [toDate, setToDate] = useState<Date>(defaultToDate());
  const [outputType, setOutputType] = useState<number>(OutputType.View);
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [compareMode, setCompareMode] = useState<number>(CompareMode.PreviousPeriod);

  const [viewData, setViewData] = useState<any>(null);
  const [viewError, setViewError] = useState<string | null>(null);

  useEffect(() => {
    if ((window as any).languageContext) {
      setLanguage((window as any).languageContext.language);
    }
  }, []);

  const resolveCountryId = useCallback(async (): Promise<string | null> => {
    const fromWindow = (window as any).current?.companies?.countryId;
    if (fromWindow) return fromWindow;
    if (!companyId) return null;
    try {
      const res = await getDetailCompanies(companyId);
      const data = res?.data ?? res;
      const company = data?.company ?? data;
      const id = company?.countryId ?? company?.country?.id ?? null;
      return id;
    } catch {
      return null;
    }
  }, [companyId]);

  useEffect(() => {
    let cancelled = false;
    resolveCountryId().then((id) => {
      if (!cancelled) {
        setCountryId(id);
        setCountryIdChecked(true);
      }
    });
    return () => { cancelled = true; };
  }, [resolveCountryId]);

  useEffect(() => {
    if (!companyId) return;
    getFinancialReportOptions(companyId, locationId)
      .then((opts) => {
        setOptions(opts);
        setTransactionMonths(opts.transactionMonths ?? []);
      })
      .catch(() => {
        setOptions(null);
        setTransactionMonths([]);
      });
  }, [companyId, locationId]);

  const resolveRangeToTransactionMonths = useCallback((): {
    fromTransactionMonthId: string;
    toTransactionMonthId: string;
  } | null => {
    if (transactionMonths.length === 0) return null;
    const fromTime = fromDate.getTime();
    const toTime = toDate.getTime();
    const overlapping = transactionMonths.filter((m) => {
      const start = new Date(m.startDate).getTime();
      const end = new Date(m.endDate).getTime();
      return fromTime <= end && toTime >= start;
    });
    if (overlapping.length === 0) return null;
    const sorted = [...overlapping].sort(
      (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    );
    return {
      fromTransactionMonthId: sorted[0].id,
      toTransactionMonthId: sorted[sorted.length - 1].id,
    };
  }, [transactionMonths, fromDate, toDate]);

  const buildPayload = useCallback((): GenerateReportPayload | null => {
    if (!countryId || !companyId) return null;
    const range = resolveRangeToTransactionMonths();
    if (!range) return null;
    const compareOff = !compareEnabled;
    // When compare is off, send only compareMode: None; omit compare range ids to avoid stale state
    const payload: GenerateReportPayload = {
      companyId,
      countryId,
      reportType,
      outputType,
      periodMode: PeriodMode.Range,
      fromTransactionMonthId: range.fromTransactionMonthId,
      toTransactionMonthId: range.toTransactionMonthId,
      compareMode: compareOff ? CompareMode.None : compareMode,
      includeZeroLines: false,
      includeDraft: false,
      includeCancelled: false,
      includeRejected: false,
    };
    return payload;
  }, [
    countryId,
    companyId,
    reportType,
    outputType,
    compareEnabled,
    compareMode,
    resolveRangeToTransactionMonths,
  ]);

  const canGenerate =
    Boolean(companyId) &&
    countryIdChecked &&
    countryId != null &&
    options != null &&
    resolveRangeToTransactionMonths() != null &&
    reportType != null;

  const handleGenerate = async (e?: React.MouseEvent) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!companyId) {
      showToast({
        message: language?.["Please select a company and try again."] ?? "Please select a company and try again.",
        type: "error",
      });
      return;
    }
    if (!countryId) {
      showToast({ message: COUNTRY_ERROR_MSG, type: "error" });
      return;
    }
    const range = resolveRangeToTransactionMonths();
    if (!range) {
      showToast({
        message:
          language?.["No transaction data exists for the selected date range."] ??
          "No transaction data exists for the selected date range.",
        type: "error",
      });
      return;
    }
    const payload = buildPayload();
    if (!payload) {
      showToast({
        message: language?.["Unable to generate report. Please check your selection and try again."] ?? "Unable to generate report. Please check your selection and try again.",
        type: "error",
      });
      return;
    }
    setLoading(true);
    setViewError(null);
    webLoadingTrue();
    try {
      const result = await generateFinancialReport(payload);
      const res = result as any;
      if (res?.code === "REPORT_TYPE_NOT_IMPLEMENTED") {
        setViewError(
          language?.["This report type is not yet available."] ??
            "This report type is not yet available."
        );
        setViewData(null);
        return;
      }
      if (outputType === OutputType.View) {
        setViewData(res);
      } else {
        const exportRes = res as ExportResponse;
        if (exportRes?.success && exportRes?.base64) {
          const bin = atob(exportRes.base64);
          const arr = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
          const blob = new Blob([arr], { type: exportRes.contentType || "application/octet-stream" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = exportRes.filename || "report";
          a.click();
          URL.revokeObjectURL(url);
          showToast({
            message: language?.["Report downloaded."] ?? "Report downloaded.",
            type: "success",
          });
        } else {
          showToast({
            message: language?.["Export failed. Please try again."] ?? "Export failed. Please try again.",
            type: "error",
          });
        }
      }
    } catch (err: any) {
      const shown = showApiError(err, {
        language,
        titleFallback: language?.["Failed to generate report."] ?? "Failed to generate report.",
      });
      setViewError(shown.message);
      setViewData(null);
    } finally {
      setLoading(false);
      webLoadingFalse();
    }
  };

  const handleReset = () => {
    setReportType(ReportType.IncomeStatement);
    setFromDate(defaultFromDate());
    setToDate(defaultToDate());
    setOutputType(OutputType.View);
    setCompareEnabled(false);
    setCompareMode(CompareMode.PreviousPeriod);
    setViewData(null);
    setViewError(null);
  };

  const title =
    language?.["Financial Reports"] ?? "Financial Reports";
  const generateLabel = language?.["Generate Report"] ?? "Generate Report";
  const resetLabel = language?.["Reset"] ?? "Reset";
  const reportTypeLabel = language?.["Report Type"] ?? "Report Type";
  const fromDateLabel = language?.["From Date"] ?? "From Date";
  const toDateLabel = language?.["To Date"] ?? "To Date";
  const exportTypeLabel = language?.["Export Type"] ?? "Export Type";
  const compareLabel = language?.["Compare"] ?? "Compare";
  const comparePreviousPeriod =
    language?.["Previous Period"] ?? "Previous Period";
  const comparePreviousYear =
    language?.["Previous Year"] ?? "Previous Year";

  if (!countryIdChecked) {
    return (
      <div className="h-full relative p-4">
        <p style={{ color: "#222525" }} className="text-[24px] font-medium font-inter">
          {title}
        </p>
        <div className="mt-4 text-[14px]" style={{ color: "#4A5353" }}>
          {language?.["Loading..."] ?? "Loading..."}
        </div>
      </div>
    );
  }

  if (countryIdChecked && !countryId) {
    return (
      <div className="h-full relative p-4">
        <p style={{ color: "#222525" }} className="text-[24px] font-medium font-inter">
          {title}
        </p>
        <div
          className="mt-4 p-4 rounded-xl border"
          style={{ borderColor: "#EBF0F0", color: "#4A5353" }}
        >
          {COUNTRY_ERROR_MSG}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full relative">
      <p
        style={{ color: "#222525" }}
        className="text-[24px] leading-[130%] font-medium font-inter"
      >
        {title}
      </p>

      <div
        className="mt-6 p-4 rounded-xl border flex flex-wrap gap-4 items-end"
        style={{ borderColor: "#EBF0F0" }}
      >
        <div>
          <label className="block text-[12px] font-inter mb-1" style={{ color: "#4A5353" }}>
            {reportTypeLabel}
          </label>
          <select
            value={reportType}
            onChange={(e) => setReportType(Number(e.target.value))}
            className="outline-none border rounded-lg px-3 py-2 text-[14px] font-inter min-w-[180px]"
            style={{ borderColor: "#EBF0F0", color: "#222525" }}
          >
            {[ReportType.IncomeStatement, ReportType.BalanceSheet, ReportType.CashFlow, ReportType.TrialBalance, ReportType.ArAgeing, ReportType.ApAgeing, ReportType.ControlAccountReconciliation].map(
              (v) => (
                <option key={v} value={v}>
                  {language?.[REPORT_TYPE_LABELS[v]] ?? REPORT_TYPE_LABELS[v] ?? `Report ${v}`}
                </option>
              )
            )}
          </select>
        </div>

        <div>
          <label className="block text-[12px] font-inter mb-1" style={{ color: "#4A5353" }}>
            {fromDateLabel}
          </label>
          <DatePicker
            selected={fromDate}
            onChange={(d: Date | null) => { if (d != null) setFromDate(d); }}
            className="border rounded-lg px-3 py-2 text-[14px] font-inter min-w-[140px]"
            dateFormat="dd/MM/yyyy"
          />
        </div>

        <div>
          <label className="block text-[12px] font-inter mb-1" style={{ color: "#4A5353" }}>
            {toDateLabel}
          </label>
          <DatePicker
            selected={toDate}
            onChange={(d: Date | null) => { if (d != null) setToDate(d); }}
            className="border rounded-lg px-3 py-2 text-[14px] font-inter min-w-[140px]"
            dateFormat="dd/MM/yyyy"
          />
        </div>

        <div>
          <label className="block text-[12px] font-inter mb-1" style={{ color: "#4A5353" }}>
            {exportTypeLabel}
          </label>
          <select
            value={outputType}
            onChange={(e) => setOutputType(Number(e.target.value))}
            className="outline-none border rounded-lg px-3 py-2 text-[14px] font-inter min-w-[160px]"
            style={{ borderColor: "#EBF0F0", color: "#222525" }}
          >
            {[OutputType.View, OutputType.Pdf, OutputType.Excel, OutputType.Csv].map(
              (v) => (
                <option key={v} value={v}>
                  {OUTPUT_TYPE_LABELS[v]}
                </option>
              )
            )}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="compare-toggle"
            checked={compareEnabled}
            onChange={(e) => setCompareEnabled(e.target.checked)}
            className="rounded border"
            style={{ borderColor: "#EBF0F0" }}
          />
          <label htmlFor="compare-toggle" className="text-[14px] font-inter" style={{ color: "#222525" }}>
            {compareLabel}
          </label>
        </div>

        {compareEnabled && (
          <select
            value={compareMode}
            onChange={(e) => setCompareMode(Number(e.target.value))}
            className="outline-none border rounded-lg px-3 py-2 text-[14px] font-inter min-w-[160px]"
            style={{ borderColor: "#EBF0F0", color: "#222525" }}
          >
            <option value={CompareMode.PreviousPeriod}>{comparePreviousPeriod}</option>
            <option value={CompareMode.SamePeriodLastYear}>{comparePreviousYear}</option>
          </select>
        )}

        <div
          className="flex gap-3"
          style={{ position: "relative", zIndex: 10 }}
        >
          <button
            type="button"
            onClick={(e) => handleGenerate(e)}
            disabled={!canGenerate || loading}
            className="px-4 py-2 rounded-full text-[14px] font-medium font-inter text-white disabled:opacity-50"
            style={{ backgroundColor: "#0F7679" }}
          >
            {loading ? (language?.["Loading..."] ?? "Loading...") : generateLabel}
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="px-4 py-2 rounded-full text-[14px] font-medium font-inter border"
            style={{ borderColor: "#EBF0F0", color: "#4A5353" }}
          >
            {resetLabel}
          </button>
        </div>
      </div>

      {viewError && (
        <div
          className="mt-4 p-4 rounded-xl border"
          style={{ borderColor: "#BA0E13", color: "#BA0E13", backgroundColor: "rgba(186,14,19,0.08)" }}
        >
          {viewError}
        </div>
      )}

      {viewData && outputType === OutputType.View && !viewError && (
        <div className="mt-6 border rounded-xl overflow-hidden" style={{ borderColor: "#EBF0F0" }}>
          <div className="p-4 border-b" style={{ borderColor: "#EBF0F0", backgroundColor: "#F7F8F8" }}>
            <p className="text-[14px] font-medium font-inter" style={{ color: "#222525" }}>
              {resolveReportTitle(viewData?.reportType, language)} — {viewData?.primaryPeriod?.label ?? ""}
              {viewData?.comparePeriod ? ` vs ${viewData.comparePeriod.label}` : ""}
            </p>
          </div>
          <div className="p-4 overflow-x-auto">
            {viewData?.rows?.length > 0 ? (() => {
              // AR/AP ageing reports carry per-row `ageing` buckets — switch the table to
              // Current | 1-30 | 31-60 | 61-90 | 90+ | Total instead of the single Amount column.
              const isAgeingReport = viewData.rows.some((r: any) => r.ageing);
              return (
              <table className="w-full text-[14px] font-inter" style={{ color: "#222525" }}>
                <thead>
                  <tr className="text-left border-b" style={{ borderColor: "#9AA5A5" }}>
                    <th className="py-2 pr-4 font-medium">Line</th>
                    {isAgeingReport ? (
                      <>
                        {AGE_COLS.map((c) => (
                          <th key={c.key} className="py-2 pr-4 font-medium text-right">{c.label}</th>
                        ))}
                        <th className="py-2 pr-4 font-medium text-right">Total</th>
                      </>
                    ) : (
                      <>
                        <th className="py-2 pr-4 font-medium text-right">Amount</th>
                        {viewData.comparePeriod && (
                          <>
                            <th className="py-2 pr-4 font-medium text-right">Compare</th>
                            <th className="py-2 pr-4 font-medium text-right">Variance</th>
                            <th className="py-2 pr-4 font-medium text-right">Variance %</th>
                          </>
                        )}
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {viewData.rows.map((row: any, i: number) => {
                    if (row.isBlankRow) {
                      return <tr key={i}><td colSpan={isAgeingReport ? AGE_COLS.length + 2 : viewData.comparePeriod ? 5 : 2} className="py-2" /></tr>;
                    }
                    const lineType = row.lineType ?? 1;
                    // Match backend FinancialReportLineTypeEnum: Detail=1, Header=2, Subtotal=3, Formula=4.
                    const isHeader = lineType === 2;
                    const isSubtotal = lineType === 3;
                    const isFormula = lineType === 4;
                    const keyFormulaKeys = ["GROSS_PROFIT", "OPERATING_PROFIT", "PROFIT_BEFORE_TAX", "NET_PROFIT"];
                    const isKeyFormula = isFormula && row.formulaKey && keyFormulaKeys.includes(row.formulaKey);
                    const formatAmount = (v: number | null | undefined) => {
                      if (v == null) return "—";
                      const n = Number(v);
                      const formatted = Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                      return n < 0 ? `(${formatted})` : formatted;
                    };
                    const rowFontWeight = isHeader || isSubtotal || isFormula ? 600 : 400;
                    // Standard-form: no rule under every row. Section headers get
                    // whitespace above; subtotals/formulas get a rule above (key
                    // formulas a darker one). Detail rows have no separators.
                    const isTotalish = isSubtotal || isFormula;
                    return (
                      <tr
                        key={i}
                        style={{
                          fontWeight: rowFontWeight,
                          ...(isHeader && { fontSize: "13px" }),
                          ...(isTotalish && {
                            borderTopWidth: 1,
                            borderTopStyle: "solid",
                            borderTopColor: isKeyFormula ? "#222525" : "#D0D9D9",
                          }),
                        }}
                      >
                        <td
                          className="py-2 pr-4"
                          style={{
                            paddingLeft: isHeader ? 0 : isSubtotal || isFormula ? 8 : 16,
                            ...(isHeader && { paddingTop: 16 }),
                          }}
                        >
                          {row.lineLabel || row.lineLabelKey || row.lineCode}
                        </td>
                        {isAgeingReport ? (
                          <>
                            {row.ageing ? (
                              AGE_COLS.map((c) => (
                                <td key={c.key} className="py-2 pr-4 text-right" style={Number(row.ageing[c.key]) < 0 ? { color: "#BA0E13" } : undefined}>
                                  {formatAmount(row.ageing[c.key])}
                                </td>
                              ))
                            ) : (
                              // Non-client rows (GL control / tie-out): leave the buckets blank, show value in Total.
                              <td colSpan={AGE_COLS.length} />
                            )}
                            <td className="py-2 pr-4 text-right" style={row.amount < 0 ? { color: "#BA0E13" } : undefined}>
                              {isHeader ? "" : formatAmount(row.amount)}
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="py-2 pr-4 text-right" style={row.amount < 0 ? { color: "#BA0E13" } : undefined}>
                              {isHeader ? "" : formatAmount(row.amount)}
                            </td>
                            {viewData.comparePeriod && (
                              <>
                                <td className="py-2 pr-4 text-right" style={row.compareAmount != null && row.compareAmount < 0 ? { color: "#BA0E13" } : undefined}>
                                  {row.compareAmount != null ? formatAmount(row.compareAmount) : "—"}
                                </td>
                                <td className="py-2 pr-4 text-right" style={row.variance != null && row.variance < 0 ? { color: "#BA0E13" } : undefined}>
                                  {row.variance != null ? formatAmount(row.variance) : "—"}
                                </td>
                                <td className="py-2 pr-4 text-right">
                                  {row.variancePercent != null ? `${Number(row.variancePercent).toFixed(2)}%` : "—"}
                                </td>
                              </>
                            )}
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              );
            })() : (
              <p className="text-[14px]" style={{ color: "#4A5353" }}>
                {language?.["No data for the selected period."] ?? "No data for the selected period."}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

FinancialReports.displayName = "FinancialReports";
export default FinancialReports;
